import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../config/api';
import usePaymentStore, { PaymentMethod, Invoice, Subscription } from '../../../store/paymentStore';


// src/features/payment/api/paymentApi.ts

export interface PaymentHistoryItem {
  id: string;
  amount: number;
  status: 'succeeded' | 'failed' | 'pending' | 'canceled';
  created: string;
  description: string;
  receiptUrl?: string;
  paymentMethod: {
    type: string;
    brand?: string;
    last4?: string;
  };
  currency: string;
  refunded?: boolean;
  refundAmount?: number;
}

interface PaymentHistoryFilters {
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}

interface PaymentHistoryResponse {
  payments: PaymentHistoryItem[];
  total: number;
  summary: {
    totalAmount: number;
    successfulPayments: number;
    failedPayments: number;
    refundedAmount: number;
  };
}

export const fetchPaymentHistory = async (filters: PaymentHistoryFilters): Promise<PaymentHistoryResponse> => {
  const { setLoading, setError } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    
    // Build query parameters
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    
    const response = await apiClient.get(`${API_ENDPOINTS.GET_PAYMENT_HISTORY}?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to fetch payment history. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

export const exportPaymentHistory = async (filters: Omit<PaymentHistoryFilters, 'page' | 'limit'>): Promise<void> => {
  const { setLoading, setError } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    
    // Build query parameters
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    params.append('format', 'csv');
    
    const response = await apiClient.get(`${API_ENDPOINTS.GET_PAYMENT_HISTORY}/export?${params.toString()}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payment-history-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to export payment history. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Fetch payment methods
export const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const { setLoading, setError, setPaymentMethods } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    const response = await apiClient.get(API_ENDPOINTS.GET_PAYMENT_METHODS);
    const methods = response.data.paymentMethods;
    setPaymentMethods(methods);
    return methods;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to fetch payment methods. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Add new payment method
export const addPaymentMethod = async (paymentMethodId: string): Promise<PaymentMethod> => {
  const { setLoading, setError, addPaymentMethod } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    const response = await apiClient.post(API_ENDPOINTS.ADD_PAYMENT_METHOD, { paymentMethodId });
    const method = response.data.paymentMethod;
    addPaymentMethod(method);
    return method;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to add payment method. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Delete payment method
export const deletePaymentMethod = async (id: string): Promise<void> => {
  const { setLoading, setError, removePaymentMethod } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    await apiClient.delete(`${API_ENDPOINTS.DELETE_PAYMENT_METHOD}/${id}`);
    removePaymentMethod(id);
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to delete payment method. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Estimate tax for a given amount
export const estimateTax = async (amount: number, country?: string, state?: string, postalCode?: string): Promise<{
  success: boolean;
  amount: number;
  tax: number;
  total: number;
  taxRate: number;
  taxType: string;
  currency: string;
  error?: string;
}> => {
  const { setLoading, setError } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    
    console.log('Sending tax estimation request:', {
      amount,
      country,
      state,
      postalCode,
      endpoint: API_ENDPOINTS.ESTIMATE_TAX
    });
    
    const response = await apiClient.post(API_ENDPOINTS.ESTIMATE_TAX, {
      amount,
      country,
      state,
      postalCode
    });
    
    console.log('Tax estimation response:', response.data);
    
    return {
      success: response.data.success,
      amount: response.data.amount / 100, // Convert from cents
      tax: response.data.tax / 100, // Convert from cents
      total: response.data.total / 100, // Convert from cents
      taxRate: response.data.tax_rate,
      taxType: response.data.tax_type,
      currency: response.data.currency,
      error: response.data.error
    };
  } catch (error: any) {
    console.error('Tax estimation API error:', error);
    console.error('Error response:', error.response?.data);
    
    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to estimate tax';
    setError(errorMessage);
    
    // Return fallback with zero tax
    return {
      success: false,
      amount,
      tax: 0,
      total: amount,
      taxRate: 0,
      taxType: 'tax',
      currency: 'usd',
      error: errorMessage
    };
  } finally {
    setLoading(false);
  }
};

// Create payment intent for account balance
export const createPaymentIntent = async (amount: number): Promise<string> => {
  const { setLoading, setError } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    const response = await apiClient.post(API_ENDPOINTS.CREATE_PAYMENT_INTENT, { amount });
    return response.data.clientSecret;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to create payment. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Fetch subscription
export const fetchSubscription = async (): Promise<Subscription | null> => {
  const { setLoading, setError, setSubscription } = usePaymentStore.getState();
  
  try {
    setLoading(true);
    const response = await apiClient.get(API_ENDPOINTS.GET_SUBSCRIPTION);
    const subscription = response.data.subscription;
    setSubscription(subscription);
    return subscription;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to fetch subscription. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};